<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray"
  >
    <div
      class="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl"
    >
      <div class="text-center">
        <LogoMainLink class="mx-auto" />
        
        <!-- Error State -->
        <div v-if="error" class="mt-8">
          <div class="rounded-full w-16 h-16 bg-red-100 mx-auto flex items-center justify-center">
            <svg class="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 class="mt-4 text-xl font-semibold text-gray-900">Authentication Failed</h2>
          <p class="mt-2 text-sm text-gray-600">{{ error }}</p>
          <router-link 
            to="/login" 
            class="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cosmicPurple hover:bg-cosmicPurple/90 transition-colors"
          >
            Try again
          </router-link>
        </div>
        
        <!-- Loading State -->
        <div v-else class="mt-8">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-cosmicPurple border-t-transparent mx-auto"></div>
          <h2 class="mt-4 text-xl font-semibold text-gray-900">Processing authentication...</h2>
          <p class="mt-2 text-sm text-gray-600">Please wait while we complete your sign in.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import LogoMainLink from '@/components/common/LogoMainLink.vue'
import type { SocialAuthCallbackParams } from '@/types/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const userStore = useUserStore()
const error = ref<string | null>(null)

onMounted(async () => {
  const params = route.query as unknown as SocialAuthCallbackParams

  // Check for errors from OAuth provider
  if (params.error) {
    error.value = getErrorMessage(params.error)
    return
  }

  try {
    // Tokens arrive via httpOnly cookies (never in the URL); verify the session instead.
    if (!(await authStore.checkAuth())) {
      error.value = 'Authentication failed. Please try again.'
      return
    }

    // Check if profile completion is needed
    const isProfileComplete = params.is_profile_complete === 'true'
    const isNewUser = params.is_new_user === 'true'
    console.log('[AuthCallback] Profile complete:', isProfileComplete, 'New user:', isNewUser)

    // Small delay to ensure state is properly updated
    await new Promise(resolve => setTimeout(resolve, 200))

    if (!isProfileComplete) {
      // Redirect to profile completion page for new users or incomplete profiles
      console.log('[AuthCallback] Redirecting to /complete-profile...')
      await router.push('/complete-profile').catch(err => {
        console.error('[AuthCallback] Navigation error:', err)
        // Force navigation using window.location as fallback
        window.location.href = '/complete-profile'
      })
    } else {
      // Check for redirect after login
      const redirectTo = localStorage.getItem('redirectAfterLogin')
      if (redirectTo) {
        localStorage.removeItem('redirectAfterLogin')
        console.log('[AuthCallback] Redirecting to:', redirectTo)
        await router.push(redirectTo).catch(err => {
          console.error('[AuthCallback] Navigation error:', err)
          window.location.href = redirectTo
        })
      } else {
        // Redirect to dashboard or appropriate page based on role
        console.log('[AuthCallback] Redirecting to /...')
        await router.push('/').catch(err => {
          console.error('[AuthCallback] Navigation error:', err)
          window.location.href = '/'
        })
      }
    }
  } catch (err: any) {
    console.error('[AuthCallback] Error processing authentication callback:', err)
    error.value = err.message || 'Failed to complete authentication. Please try again.'
  }
})

/**
 * Convert error codes to user-friendly messages
 */
function getErrorMessage(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    access_denied: 'You denied access to your account. Please try again if you want to continue.',
    invalid_request: 'The authentication request was invalid. Please try again.',
    server_error: 'A server error occurred. Please try again later.',
    temporarily_unavailable: 'The authentication service is temporarily unavailable. Please try again later.'
  }
  
  return errorMessages[errorCode] || `Authentication error: ${errorCode}`
}
</script>
